import { escapeRTF } from 'util/cite';
import isUndefined from 'lodash/fp/isUndefined';
import isArray from 'lodash/fp/isArray';
import { CitationTemplateEntry } from '__generated__/graphql';

interface FormattedText {
  rtf: string;
  html: string;
  text: string;
}

interface CitationOptions {
  startPage: string;
  endPage?: string;
  footnote?: string;
  subdivisions?: string;
}

const formatFragment = (
  text: string,
  format?: CitationTemplateEntry['format'],
): FormattedText => {
  switch (format) {
    case 'italic':
      return {
        rtf: `{\\i ${escapeRTF(text)}}`,
        html: `<i>${text}</i>`,
        text,
      };
    case 'underline':
      return {
        rtf: `{\\ul ${escapeRTF(text)}}`,
        html: `<u>${text}</u>`,
        text,
      };
    default:
      return {
        rtf: escapeRTF(text),
        html: text,
        text,
      };
  }
};

const getPageRange = (
  startPage: string,
  endPage?: string,
  format?: string,
): string => {
  if (!endPage || startPage === endPage) return startPage;

  let rangeEnd = endPage;
  if (
    format !== 'california' &&
    +startPage &&
    +endPage &&
    startPage.length >= 3 &&
    startPage.substring(0, startPage.length - 2) ===
      endPage.substring(0, endPage.length - 2)
  ) {
    rangeEnd = endPage.substring(endPage.length - 2);
  }

  return `${startPage}\u2013${rangeEnd}`;
};

const getFootnoteText = (
  pages: string,
  footnote?: string,
  format?: string,
): string => {
  if (isUndefined(footnote)) return pages;
  return pages + `${format !== 'california' ? ' n.' : ', fn. '}${footnote}`;
};

const getSubdivisionsText = (
  subdivisions?: string,
  format?: string,
): string => {
  if (!subdivisions || format !== 'california') return subdivisions || '';

  const isRange = subdivisions.includes('\u2013');
  const prefix = `, ${isRange ? 'subds.' : 'subd.'} `;
  return prefix + subdivisions;
};

export function getCitation(
  citation: CitationTemplateEntry[] | null,
  format: string,
  { startPage, endPage, footnote, subdivisions }: CitationOptions,
  search?: string | RegExp,
  replace?: string,
): FormattedText {
  const cite: FormattedText = {
    rtf: '',
    html: '',
    text: '',
  };

  if (!citation) return cite;

  citation.forEach(fragment => {
    const citationFragment = isArray(fragment)
      ? { format: fragment[0], text: fragment[1] }
      : fragment;

    const fragText =
      search && replace
        ? (citationFragment.text || '').replace(search, replace)
        : citationFragment.text;

    if (fragText) {
      const formatted = formatFragment(fragText, citationFragment.format);
      cite.rtf += formatted.rtf;
      cite.html += formatted.html;
      cite.text += formatted.text;
    }

    if (fragment.typeName === 'official.at_pages') {
      const pageText = getFootnoteText(
        getPageRange(startPage, endPage, format),
        footnote,
        format,
      );
      cite.rtf += pageText;
      cite.html += pageText;
      cite.text += pageText;
    }

    if (fragment.typeName === 'subdivisions') {
      const subdivisionText = getSubdivisionsText(subdivisions, format);
      cite.rtf += escapeRTF(subdivisionText);
      cite.html += subdivisionText;
      cite.text += subdivisionText;
    }
  });

  return cite;
}
